import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Check, AlertCircle, ExternalLink, FileText, CreditCard } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Pillar-facing payout settings panel. Shown inside the Profile edit form
 * when the viewer is a Partnership member. Lets them pick:
 *   • Invoice  — platform collects, admin reconciles offline
 *   • Stripe   — connect their own Stripe account (hosted onboarding)
 *
 * After "Stripe" is selected, a Connect button starts the Stripe onboarding
 * flow; the user comes back to /profile?stripe_connect=return and we hit
 * /stripe/connect/status to flip the ready flag.
 */
export default function PayoutSettings({ user, onSaved }) {
    const [method, setMethod] = useState((user?.payout_method || '').toLowerCase());
    const [connecting, setConnecting] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [status, setStatus] = useState({
        connected: !!user?.stripe_account_id,
        charges_enabled: !!user?.stripe_charges_enabled,
        account_id: user?.stripe_account_id || '',
    });

    // On return from Stripe-hosted onboarding, re-check the Connect account
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('stripe_connect') === 'return' && user?.stripe_account_id) {
            refreshStatus();
        }
    }, [user?.stripe_account_id]);

    const saveMethod = async (next) => {
        setMethod(next);
        try {
            await axios.put(`${API_URL}/api/profile`, { payout_method: next }, { withCredentials: true });
            toast.success(`Payout method set to ${next === 'stripe' ? 'Stripe' : 'Invoice'}`);
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not save');
        }
    };

    const startConnect = async () => {
        setConnecting(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/stripe/connect/onboarding-link`,
                { origin_url: window.location.origin },
                { withCredentials: true }
            );
            // Stripe-hosted onboarding opens in the SAME tab so the return_url navigates back to /profile
            window.location.href = data.url;
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not start Stripe onboarding');
            setConnecting(false);
        }
    };

    const refreshStatus = async () => {
        setRefreshing(true);
        try {
            const { data } = await axios.get(`${API_URL}/api/stripe/connect/status`, { withCredentials: true });
            setStatus(data);
            if (data.charges_enabled) toast.success('Stripe account is ready to receive payouts');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not refresh status');
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <div className="p-5 bg-[#F9F6F0] rounded-sm space-y-4" data-testid="payout-settings">
            <div>
                <h3 className="font-serif text-lg text-[#1A1A2E]">Payout method</h3>
                <p className="text-xs text-[#4A4A68] mt-1">
                    How you get paid for your Events & Library sales. The platform takes a 7% + €0.50 fee per Stripe sale. Invoice sellers are reconciled manually by the admin.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                    type="button"
                    onClick={() => saveMethod('invoice')}
                    className={`text-left p-4 rounded-sm border transition-colors ${method === 'invoice' ? 'bg-white border-[#5B2D8E] ring-1 ring-[#5B2D8E]' : 'bg-white border-[#E5E0D8] hover:bg-[#F2EBE5]'}`}
                    data-testid="payout-invoice"
                >
                    <div className="flex items-center gap-2 mb-1">
                        <FileText className="w-4 h-4 text-[#5B2D8E]" />
                        <span className="font-medium text-sm text-[#1A1A2E]">Invoice</span>
                        {method === 'invoice' && <Check className="w-4 h-4 ml-auto text-[#5B2D8E]" />}
                    </div>
                    <p className="text-xs text-[#4A4A68]">Platform collects 100% to its account. Admin reconciles your share manually (invoice-based).</p>
                </button>

                <button
                    type="button"
                    onClick={() => saveMethod('stripe')}
                    className={`text-left p-4 rounded-sm border transition-colors ${method === 'stripe' ? 'bg-white border-[#5B2D8E] ring-1 ring-[#5B2D8E]' : 'bg-white border-[#E5E0D8] hover:bg-[#F2EBE5]'}`}
                    data-testid="payout-stripe"
                >
                    <div className="flex items-center gap-2 mb-1">
                        <CreditCard className="w-4 h-4 text-[#5B2D8E]" />
                        <span className="font-medium text-sm text-[#1A1A2E]">Stripe (direct)</span>
                        {method === 'stripe' && <Check className="w-4 h-4 ml-auto text-[#5B2D8E]" />}
                    </div>
                    <p className="text-xs text-[#4A4A68]">Payments route to your own Stripe account automatically. Platform retains 7% + €0.50 as application fee.</p>
                </button>
            </div>

            {method === 'stripe' && (
                <div className="pt-3 border-t border-[#E5E0D8]">
                    {status.connected && status.charges_enabled ? (
                        <div className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-green-700 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm text-[#1A1A2E] font-medium">Stripe account connected</p>
                                <p className="text-xs text-[#4A4A68] mt-1">Account ID: <code className="text-[#5B2D8E]">{status.account_id}</code></p>
                                <button
                                    type="button"
                                    onClick={refreshStatus}
                                    disabled={refreshing}
                                    className="text-xs text-[#5B2D8E] hover:underline mt-2 inline-flex items-center gap-1"
                                    data-testid="payout-refresh-status"
                                >
                                    {refreshing && <Loader2 className="w-3 h-3 animate-spin" />}
                                    Re-check status
                                </button>
                            </div>
                        </div>
                    ) : status.connected && !status.charges_enabled ? (
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm text-[#1A1A2E] font-medium">Onboarding incomplete</p>
                                <p className="text-xs text-[#4A4A68] mt-1">Your Stripe account exists but can't accept payments yet. Finish onboarding to go live.</p>
                                <div className="flex gap-2 mt-2">
                                    <button
                                        type="button"
                                        onClick={startConnect}
                                        disabled={connecting}
                                        className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1"
                                        data-testid="payout-resume-onboarding"
                                    >
                                        {connecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
                                        Continue onboarding
                                    </button>
                                    <button
                                        type="button"
                                        onClick={refreshStatus}
                                        disabled={refreshing}
                                        className="btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1"
                                    >
                                        {refreshing && <Loader2 className="w-3 h-3 animate-spin" />}
                                        Re-check
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={startConnect}
                            disabled={connecting}
                            className="btn-primary text-sm px-4 py-2 inline-flex items-center gap-2"
                            data-testid="payout-connect-stripe"
                        >
                            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                            Connect your Stripe account
                        </button>
                    )}
                    <p className="text-xs text-[#4A4A68] mt-3">
                        Stripe will walk you through a short identity check (KYC) and ask for your bank details — payouts land directly in your account. The platform never sees your private keys.
                    </p>
                </div>
            )}
        </div>
    );
}
